import type { ReactNode } from 'react';
import { Skeleton } from '@/components/ui/skeleton';

export interface BandMetric {
  label: string;
  value: ReactNode;
  /** Sufixo pequeno colado ao valor (ex.: "ms"). */
  unit?: string;
  /** Contexto curto ao lado do valor (ex.: "de 89", "meta 99%"). */
  hint?: ReactNode;
  /** Cor do valor quando ele carrega estado (erro, meta atingida). */
  valueClassName?: string;
  onClick?: () => void;
}

interface MetricBandProps {
  title: string;
  caption: string;
  metrics: BandMetric[];
  loading?: boolean;
  /** Quando presente, substitui as células por uma mensagem de erro. */
  error?: ReactNode;
}

/** Empilha as faixas de métricas do dashboard, sem moldura própria. */
export function MetricBandGroup({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-3">{children}</div>;
}

function MetricCell({ metric, loading }: { metric: BandMetric; loading?: boolean }) {
  const content = (
    <>
      <span className="truncate text-sm text-text-secondary">{metric.label}</span>
      {loading ? (
        <Skeleton className="h-7 w-14" />
      ) : (
        <span className="flex min-w-0 items-baseline gap-2">
          <span
            className={`text-2xl font-bold leading-none tracking-tight ${
              metric.valueClassName ?? 'text-text-title'
            }`}
          >
            {metric.value}
            {metric.unit && (
              <span className="ml-1 text-sm font-semibold text-text-body">{metric.unit}</span>
            )}
          </span>
          {metric.hint && (
            <span className="truncate text-xs text-text-secondary">{metric.hint}</span>
          )}
        </span>
      )}
    </>
  );

  const base = 'flex min-w-0 flex-col justify-center gap-2 bg-card px-4 py-3 text-left';

  if (metric.onClick) {
    return (
      <button
        type="button"
        onClick={metric.onClick}
        className={`${base} transition-colors hover:bg-elevated focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40`}
      >
        {content}
      </button>
    );
  }

  return <div className={base}>{content}</div>;
}

/** Faixa de métricas: rótulo do grupo à esquerda + células compactas lado a lado. */
export function MetricBand({ title, caption, metrics, loading, error }: MetricBandProps) {
  return (
    <section
      aria-label={title}
      className="flex flex-col overflow-hidden rounded-lg border border-border bg-border xl:flex-row"
    >
      <div className="flex shrink-0 flex-col justify-center gap-0.5 bg-card px-4 py-2.5 xl:w-36 xl:py-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
          {title}
        </h2>
        <p className="text-xs text-text-hint">{caption}</p>
      </div>

      {error ? (
        <div className="flex flex-1 items-center bg-card px-4 py-3 text-sm text-error mt-px xl:ml-px xl:mt-0">
          {error}
        </div>
      ) : (
        <div className="mt-px grid flex-1 grid-cols-2 gap-px sm:grid-cols-5 xl:ml-px xl:mt-0 [&>*:last-child:nth-child(odd)]:col-span-2 sm:[&>*:last-child:nth-child(odd)]:col-span-1">
          {metrics.map((metric) => (
            <MetricCell key={metric.label} metric={metric} loading={loading} />
          ))}
        </div>
      )}
    </section>
  );
}
